import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AdminServices } from '../services/admin-services';
import {
  AdminSessionContext,
  phaseOf,
  watchUnauthorized,
  type AdminSessionValue,
  type SessionStatus,
} from './admin-session';

export interface AdminSessionProviderProps {
  readonly services: AdminServices;
  readonly children: ReactNode;
}

/** Estado de la sesion de administrador; la cookie `HttpOnly` la gestiona el navegador. */
export function AdminSessionProvider({ services, children }: AdminSessionProviderProps) {
  const [status, setStatus] = useState<SessionStatus>({ phase: 'checking' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void services.auth.state().then((result) => {
      if (cancelled) return;
      setStatus(result.ok ? phaseOf(result.value) : { phase: 'unreachable', error: result.error });
    });
    return () => {
      cancelled = true;
    };
  }, [services, attempt]);

  const expire = useCallback(() => {
    setStatus({ phase: 'login', expired: true });
  }, []);

  const wrapped = useMemo<AdminServices>(
    () => ({
      auth: services.auth,
      admin: watchUnauthorized(services.admin, expire),
      aiSettings: watchUnauthorized(services.aiSettings, expire),
    }),
    [services, expire],
  );

  const value = useMemo<AdminSessionValue>(
    () => ({
      status,
      services: wrapped,
      refresh: () => {
        setStatus({ phase: 'checking' });
        setAttempt((current) => current + 1);
      },
      signedIn: (state) => {
        setStatus(phaseOf(state));
      },
      logout: async () => {
        await services.auth.logout();
        setStatus({ phase: 'login', expired: false });
      },
    }),
    [status, wrapped, services],
  );

  return <AdminSessionContext value={value}>{children}</AdminSessionContext>;
}
